import { useState, useEffect, useMemo } from 'react'
import { useAtom } from 'jotai'
import { ChatHeader } from './ChatHeader'
import { useAiChat } from './ai/useAiChat'
import {
  Text,
  GreyGrey60,
  Primary12Regular,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import {
  useTableToChat,
  chatSelectedContextItemsAtom,
  chatCurrentTableSelectionAtom
} from '../../../../context/TableToChatContext'
import { useAutoContextSelection } from './useAutoContextSelection'
import { ChatInputBox } from './ChatInputBox'
import { ChatMessages } from './ChatMessages'
import {
  chatInputDraftAtom,
  chatMessagesAtom
} from '../../../../context/TableToChatContext'
import { useRef } from 'react'

interface ChatProps {
  revenueWaterfallData?: any
  revenueWaterfallFilters?: any
  currency?: string
}

export const Chat = ({
  revenueWaterfallData,
  revenueWaterfallFilters,
  currency
}: ChatProps = {}) => {
  const [selectedContextItems, setSelectedContextItems] = useAtom(
    chatSelectedContextItemsAtom
  )
  // Persisted draft input text
  const [draftValue, setDraftValue] = useAtom(chatInputDraftAtom)
  const [persistedMessages, setPersistedMessages] = useAtom(chatMessagesAtom)
  const hydrated = useRef(false)
  const { lastAddedSelection } = useTableToChat()

  // Keep track of the current table selection for pills
  const [currentTableSelection, setCurrentTableSelection] = useAtom(
    chatCurrentTableSelectionAtom
  )

  // Fetch data for detailed context
  const customersQuery = useQuery(dashboard20240730Client.getCustomers, {
    limit: 100
  })

  const productsQuery = useQuery(dashboardv99990101Client.getProducts, {
    limit: 100
  })

  // Memoize arrays to prevent useAiChat from recreating on every render
  const customers = useMemo(
    () => customersQuery.data?.items || [],
    [customersQuery.data?.items]
  )
  const products = useMemo(
    () => productsQuery.data?.items || [],
    [productsQuery.data?.items]
  )

  // Auto-select relevant context items based on table selection
  useAutoContextSelection({
    tableSelection: currentTableSelection,
    customers,
    products,
    onContextItemsChange: setSelectedContextItems,
    selectedContextItems,
    revenueWaterfallData
  })

  // Memoize context data object to prevent useAiChat recreation
  const contextData = useMemo(
    () => ({
      selectedContextItems,
      revenueWaterfallData,
      revenueWaterfallFilters,
      currency,
      customers,
      products,
      tableSelection: currentTableSelection
    }),
    [
      selectedContextItems,
      revenueWaterfallData,
      revenueWaterfallFilters,
      currency,
      customers,
      products,
      currentTableSelection
    ]
  )

  const {
    messages,
    isLoading,
    currentStatus,
    statusMessage,
    handleSubmit,
    setMessages
  } = useAiChat(contextData)

  // Hydrate messages from persisted atom on first mount
  useEffect(() => {
    if (!hydrated.current) {
      if (persistedMessages && persistedMessages.length > 0) {
        setMessages(persistedMessages)
      }
      hydrated.current = true
    }
  }, [persistedMessages, setMessages])

  // Keep atom in sync with current messages
  useEffect(() => {
    setPersistedMessages(messages)
  }, [messages, setPersistedMessages])

  // Listen for table selections to possibly trigger side effects later
  // Do not prefill the chat input text; context pills are handled elsewhere.
  useEffect(() => {
    if (!lastAddedSelection) return
  }, [lastAddedSelection])

  // Clear stored table selection when table selection pill is removed
  useEffect(() => {
    if (!selectedContextItems.has('table-selection:current')) {
      setCurrentTableSelection(null)
    }
  }, [selectedContextItems, setCurrentTableSelection])

  const handleDelete = () => {
    setMessages([])
  }

  // Wrap submit to clear draft value after sending when controlled
  const handleSubmitAndClear = (msg: string) => {
    handleSubmit(msg)
    setDraftValue('')
  }

  // Helper function to describe table selection
  const getTableSelectionDescription = (
    selection: typeof lastAddedSelection
  ) => {
    if (!selection) return 'Unknown selection'

    const { rowCount, columnCount, rowLabels, columnLabels } = selection

    if (rowCount === 1 && columnCount === 1) {
      return `${rowLabels[0]} - ${columnLabels[0]}`
    } else if (rowCount === 1) {
      return `${rowLabels[0]} across ${columnCount} columns (${columnLabels.join(', ')})`
    } else if (columnCount === 1) {
      return `${columnLabels[0]} for ${rowCount} rows (${rowLabels.join(', ')})`
    } else {
      return `${rowCount} rows × ${columnCount} columns selection`
    }
  }

  return (
    <Box backgroundColor="white" position="relative" width="100%" height="100%">
      <Flex
        direction="column"
        align="flex-start"
        justify="flex-start"
        position="relative"
        width="100%"
        height="100%"
        p={0}
      >
        <ChatHeader chatTitle="New chat" onDelete={handleDelete} />

        <Box
          flex={1}
          minHeight="1px"
          minWidth="1px"
          position="relative"
          width="100%"
        >
          <Box position="relative" width="100%" height="100%">
            <Flex
              direction="column"
              align="flex-start"
              justify="flex-start"
              p={2}
              position="relative"
              width="100%"
              height="100%"
            >
              <ChatMessages
                messages={messages}
                currentStatus={currentStatus}
                statusMessage={statusMessage}
              />

              <Box width="100%" position="relative">
                <Box width="100%" height="100%" position="relative">
                  <Flex
                    direction="column"
                    gap={4}
                    align="flex-start"
                    justify="flex-start"
                    position="relative"
                    width="100%"
                  >
                    <Box width="100%" position="relative">
                      <Flex
                        direction="column"
                        align="center"
                        justify="flex-start"
                        width="100%"
                      >
                        <ChatInputBox
                          value={draftValue}
                          onChangeValue={setDraftValue}
                          initialValue={''}
                          onSubmit={handleSubmitAndClear}
                          disabled={isLoading}
                          sendDisabled={isLoading}
                          selectedContextItems={selectedContextItems}
                          onContextItemsChange={setSelectedContextItems}
                          revenueWaterfallData={revenueWaterfallData}
                          tableSelection={currentTableSelection}
                        />
                      </Flex>
                    </Box>

                    <AIDisclaimer />
                  </Flex>
                </Box>
              </Box>
            </Flex>
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}

const AIDisclaimer = () => (
  <Box textAlign="center" width="100%">
    <Text {...Primary12Regular} color={GreyGrey60}>
      Sequence AI can make mistakes. Check important info.
    </Text>
  </Box>
)
